import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import Typography from 'components/core/Typography';
// Models
import { serializePercentage } from 'models/cpm/schemas';
// Custom
import {
  cpmPriceGridDataShape, errorShape,
} from 'constants/PropTypesShapes';
import { buildFormErrors, cpmPriceGridParkingCategoryConstraints } from 'utils/Forms';
import { sanitizeFloatRounded } from 'utils/Tools';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import NumberInputField from 'components/inputFields/NumberInputField';
import ParkingCategorySelectField from 'cpmCarparks/assets/parkingCategories/ParkingCategorySelectField';
import ItemsSelectField from 'components/inputFields/ItemsSelectField';
import { PRICE_GRID_CALCULATION_MODES } from 'constants/App';

class PriceGridAddParkingCategoryForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        parkingCategoryId, discount, priceGridCalculationMode,
      },
      priceGrid, onErrorsChange, onValidate,
    } = this.props;

    const errors = buildFormErrors({ parkingCategoryId, discount, priceGridCalculationMode }, cpmPriceGridParkingCategoryConstraints);
    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      onValidate({
        parkingCategoryId,
        discount: serializePercentage(discount),
        calculationMode: priceGridCalculationMode,
      }, priceGrid);
      return true;
    }
    return false;
  }

  render() {
    const {
      fields: {
        parkingCategoryId, discount, priceGridCalculationMode,
      }, errors,
      onFieldsChange,
    } = this.props;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: <LocalizedMessage id="help.parkingCategoriesPriceGrid.parkingCategory" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="field.parkingCategory" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    <div className="col-12 col-lg-8 d-flex">
                      <ParkingCategorySelectField
                        errorText={_get(errors, 'parkingCategoryId')}
                        value={parkingCategoryId}
                        required
                        onChange={(value) => onFieldsChange({ parkingCategoryId: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
          <div className="col-12 pt-3">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: <LocalizedMessage id="help.parkingCategoriesPriceGrid.discount" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 col-lg-6">
                      <NumberInputField
                        label={<LocalizedMessage id="field.discountApplied" />}
                        errorText={_get(errors, 'discount')}
                        min={0}
                        max={100}
                        value={discount}
                        required
                        sanitizer={sanitizeFloatRounded(2)}
                        inputMaxWidth={70}
                        adornments={{ start: '%' }}
                        onChange={(value) => onFieldsChange({ discount: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
          <div className="col-12 pt-3">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: <LocalizedMessage id="help.parkingCategoriesPriceGrid.calculationMode" />,
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 col-lg-8">
                      <ItemsSelectField
                        labelId="field.priceGridCalculationMode"
                        errorText={_get(errors, 'priceGridCalculationMode')}
                        value={priceGridCalculationMode}
                        items={PRICE_GRID_CALCULATION_MODES}
                        required
                        onChange={(value) => onFieldsChange({ priceGridCalculationMode: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

PriceGridAddParkingCategoryForm.propTypes = {
  priceGrid: cpmPriceGridDataShape.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

PriceGridAddParkingCategoryForm.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  parkingCategoryId: {
    defaultValue: null,
  },
  discount: {
    defaultValue: 0,
  },
  priceGridCalculationMode: {
    defaultValue: 'PRORATED',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: ownProps.operationError,
});

const mapStateToProps = (state) => ({
  operationError: state.cpmCarparkParkingCategory.addPriceGrid.error,
});

module.exports = compose(
  connect(
    mapStateToProps,
  ),
  withFormManagement(
    mapFieldsToProps,
    mapOwnPropsToResources,
    false,
  ),
  withStateReinit(['addCarparkPriceGridParkingCategory']),
)(PriceGridAddParkingCategoryForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/priceGrids/PriceGridAddParkingCategoryForm.js